import { ZERO_ROTATION, type CartesianPose } from '@sb/geometry';

export const ORHEXE_TARGET_ADDRESS = 0x40;

export enum Register {
  // Write-only
  ZERO = 0x0000, // Zero force and torque values

  // Read-only
  STATUS = 0x0101, // 0 when no error
  FORCE_X = 0x0103, // 1/10 N
  FORCE_Y = 0x0104, // 1/10 N
  FORCE_Z = 0x0105, // 1/10 N
  TORQUE_X = 0x0106, // 1/100 Nm
  TORQUE_Y = 0x0107, // 1/100 Nm
  TORQUE_Z = 0x0108, // 1/100 Nm
}

export enum OR_HEX_E_ZERO_COMMAND {
  UNZERO = 0x0000,
  ZERO = 0x0001,
}

/**
 * Robot-side mounting plane to tool-side mounting plane, in meters — the
 * sensor's contribution to the tooltip chain. 50.0 mm, matching the CAD
 * `hex_e.glb` is exported from and OnRobot's quoted 50 x 71 x 93 mm.
 *
 * Everything that needs the sensor's spacing composes this one value, so the
 * drawn chain and the commanded chain cannot disagree.
 */
export const OR_HEX_E_TOOLTIP_Z_WITHOUT_TOOL_CHANGER = 0.05;

/**
 * Net height an OnRobot tool changer adds between the sensor's integrated QC
 * and the tool — less than its 28.4 mm body, since the upper section recesses
 * into the sensor. Measured from `qc_tool_side.glb`, whose tool face sits
 * 17.2 mm below the origin that meets the sensor.
 */
export const OR_HEX_E_TOOL_CHANGER_STACK_HEIGHT = 0.0172;

export const OR_HEX_E_TOOLTIP_Z_WITH_TOOL_CHANGER =
  OR_HEX_E_TOOLTIP_Z_WITHOUT_TOOL_CHANGER + OR_HEX_E_TOOL_CHANGER_STACK_HEIGHT;

// Default tooltip transform: tool changer disabled (toggle off). Snapshotted
// into the config when a HEX-E row is created, so changing the constant does
// not move rows that already exist.
export const OR_HEX_E_DEFAULT_TOOLTIP_TRANSFORM = {
  ...ZERO_ROTATION,
  x: 0,
  y: 0,
  z: OR_HEX_E_TOOLTIP_Z_WITHOUT_TOOL_CHANGER,
};

/**
 * Resolves the effective HEX-E TCP transform. The Z offset is fully derived
 * from the "Enable OnRobot Tool Changer" toggle, overriding whatever Z is
 * stored on the config. This keeps motion, device state, and the UI in sync
 * even for legacy configs whose stored `tcpTransform.z` predates the toggle
 * (e.g. the old 0.05 default or a hand-set value) and could otherwise drift
 * from the toggle state.
 */
export function resolveOnRobotHexETcpTransform(
  tcpTransform: CartesianPose,
  enableToolChanger: boolean,
): CartesianPose {
  return {
    ...tcpTransform,
    z: enableToolChanger
      ? OR_HEX_E_TOOLTIP_Z_WITH_TOOL_CHANGER
      : OR_HEX_E_TOOLTIP_Z_WITHOUT_TOOL_CHANGER,
  };
}

/** Sensor mass used by the kinematics. No URDF in this repo to check against. */
export const OR_HEX_E_MASS_KG = 0.347;

// Default collision box in config coordinates (meters)
export const OR_HEX_E_DEFAULT_COLLISION_BOX = {
  origin: { x: 0, y: 0, z: 0.06 / 2 },
  size: { x: 0.115, y: 0.115, z: 0.06 },
} as const;
